import {
	collection,
	doc,
	getDoc,
	getDocs,
	getCountFromServer,
	updateDoc,
	query,
	where,
	orderBy,
	limit as firestoreLimit,
	Timestamp,

	QueryConstraint
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
	AgentInsight,
	InsightPriority,
	InsightStatus,
	InsightTrend,
	InsightType,
	INSIGHT_PRIORITIES,
	INSIGHT_TYPES
} from '../types/agent';

export interface InsightStats {
	totalInsights: number;
	newInsights: number;
	urgentInsights: number;
	highPriorityInsights: number;
	averageLeadScore: number;
	conversionRate: number;
	insightsByType: Record<InsightType, number>;
	insightsByPriority: Record<InsightPriority, number>;
	/**
	 * True when the collection held more documents than STATS_SCAN_LIMIT, so the
	 * distribution/average figures are computed from the most recent slice while
	 * `totalInsights` remains exact (server-side count).
	 */
	sampled: boolean;
}

export class InsightService {
	/**
	 * Hard cap on documents pulled into the browser for aggregate views.
	 * Master/super agents pass no agentId, so without this an unfiltered read of
	 * the entire agentInsights collection ran on every dashboard load.
	 */
	private static readonly STATS_SCAN_LIMIT = 1000;

	// Get insights for an agent
	static async getAgentInsights(
		agentId: string,
		filters?: {
			status?: InsightStatus;
			priority?: InsightPriority;
			type?: InsightType;
			dateRange?: { start: Date; end: Date };
			limit?: number;
		}
	): Promise<AgentInsight[]> {
		try {
			const constraints: QueryConstraint[] = [
				where('agent_id', '==', agentId)
			];

			if (filters?.status) {
				constraints.push(where('status', '==', filters.status));
			}

			if (filters?.priority) {
				constraints.push(where('priority', '==', filters.priority));
			}

			if (filters?.type) {
				constraints.push(where('type', '==', filters.type));
			}

			if (filters?.dateRange) {
				constraints.push(
					where('createdAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
					where('createdAt', '<=', Timestamp.fromDate(filters.dateRange.end))
				);
			}

			constraints.push(orderBy('createdAt', 'desc'));

			if (filters?.limit) {
				constraints.push(firestoreLimit(filters.limit));
			}

			const q = query(collection(db, 'agentInsights'), ...constraints);
			const snapshot = await getDocs(q);

			return snapshot.docs.map(doc => {
				const data = doc.data();
				return {
					id: doc.id,
					...data,
					createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
					updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt || new Date()),
					expiresAt: data.expiresAt?.toDate ? data.expiresAt.toDate() : (data.expiresAt || new Date()),
					resolvedAt: data.resolvedAt?.toDate ? data.resolvedAt.toDate() : data.resolvedAt,
					viewedAt: data.viewedAt?.toDate ? data.viewedAt.toDate() : data.viewedAt
				} as AgentInsight;
			});
		} catch (error) {
			console.error('Error getting agent insights:', error);
			throw error;
		}
	}

	// Get all insights (for master agents)
	static async getAllInsights(filters?: {
		status?: InsightStatus;
		priority?: InsightPriority;
		type?: InsightType;
		limit?: number;
	}): Promise<AgentInsight[]> {
		try {
			const constraints: QueryConstraint[] = [];

			if (filters?.status) {
				constraints.push(where('status', '==', filters.status));
			}

			if (filters?.priority) {
				constraints.push(where('priority', '==', filters.priority));
			}

			if (filters?.type) {
				constraints.push(where('type', '==', filters.type));
			}

			constraints.push(orderBy('createdAt', 'desc'));

			if (filters?.limit) {
				constraints.push(firestoreLimit(filters.limit));
			}

			const q = query(collection(db, 'agentInsights'), ...constraints);
			const snapshot = await getDocs(q);

			return snapshot.docs.map(doc => {
				const data = doc.data();
				return {
					id: doc.id,
					...data,
					createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
					updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt || new Date()),
					expiresAt: data.expiresAt?.toDate ? data.expiresAt.toDate() : (data.expiresAt || new Date()),
					resolvedAt: data.resolvedAt?.toDate ? data.resolvedAt.toDate() : data.resolvedAt,
					viewedAt: data.viewedAt?.toDate ? data.viewedAt.toDate() : data.viewedAt
				} as AgentInsight;
			});
		} catch (error) {
			console.error('Error getting all insights:', error);
			throw error;
		}
	}

	// Get insight by ID
	static async getInsight(insightId: string): Promise<AgentInsight | null> {
		try {
			const insightDoc = await getDoc(doc(db, 'agentInsights', insightId));
			if (insightDoc.exists()) {
				const data = insightDoc.data();
				return {
					id: insightDoc.id,
					...data,
					createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date()),
					updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt || new Date()),
					expiresAt: data.expiresAt?.toDate ? data.expiresAt.toDate() : (data.expiresAt || new Date()),
					resolvedAt: data.resolvedAt?.toDate ? data.resolvedAt.toDate() : data.resolvedAt,
					viewedAt: data.viewedAt?.toDate ? data.viewedAt.toDate() : data.viewedAt
				} as AgentInsight;
			}
			return null;
		} catch (error) {
			console.error('Error getting insight by ID:', error);
			throw error;
		}
	}

	// Update insight status
	static async updateInsightStatus(insightId: string, status: InsightStatus): Promise<void> {
		try {
			await updateDoc(doc(db, 'agentInsights', insightId), {
				status,
				updatedAt: Timestamp.now()
			});
		} catch (error) {
			console.error('Error updating insight status:', error);
			throw error;
		}
	}

	// Update insight fields (status, priority, leadScore)
	static async updateInsight(insightId: string, updates: { status?: InsightStatus; priority?: InsightPriority; leadScore?: number }): Promise<void> {
		try {
			await updateDoc(doc(db, 'agentInsights', insightId), {
				...updates,
				updatedAt: Timestamp.now()
			});
		} catch (error) {
			console.error('Error updating insight:', error);
			throw error;
		}
	}

	// Mark insight as viewed
	static async markInsightAsViewed(insightId: string): Promise<void> {
		try {
			await updateDoc(doc(db, 'agentInsights', insightId), {
				viewed: true,
				viewedAt: Timestamp.now(),
				updatedAt: Timestamp.now()
			});
		} catch (error) {
			console.error('Error marking insight as viewed:', error);
			throw error;
		}
	}

	// Get insights statistics
	static async getInsightStats(agentId?: string): Promise<InsightStats> {
		try {
			const filterConstraints: QueryConstraint[] = [];
			if (agentId) {
				filterConstraints.push(where('agent_id', '==', agentId));
			}

			// Exact total from the server; only the distribution work needs documents.
			const countPromise = getCountFromServer(
				query(collection(db, 'agentInsights'), ...filterConstraints)
			);

			// Bounded scan of the most recent insights. Master/super agents pass no
			// agentId, so this query is otherwise the whole collection.
			const scanPromise = getDocs(query(
				collection(db, 'agentInsights'),
				...filterConstraints,
				orderBy('createdAt', 'desc'),
				firestoreLimit(this.STATS_SCAN_LIMIT)
			));

			const [countSnapshot, snapshot] = await Promise.all([countPromise, scanPromise]);

			const totalInsights = countSnapshot.data().count;
			const insights = snapshot.docs.map(doc => doc.data() as AgentInsight);

			return {
				totalInsights,
				newInsights: insights.filter(i => i.status === 'new').length,
				urgentInsights: insights.filter(i => i.priority === 'urgent').length,
				highPriorityInsights: insights.filter(i => i.priority === 'high').length,
				averageLeadScore: insights.length > 0
					? insights.reduce((sum, i) => sum + (i.leadScore || 0), 0) / insights.length
					: 0,
				conversionRate: insights.length > 0
					? (insights.filter(i => i.status === 'converted').length / insights.length) * 100
					: 0,
				insightsByType: this.countByKey(insights, 'type', INSIGHT_TYPES),
				insightsByPriority: this.countByKey(insights, 'priority', INSIGHT_PRIORITIES),
				sampled: totalInsights > insights.length
			};
		} catch (error) {
			console.error('InsightService.getInsightStats: Error:', error);
			throw error;
		}
	}

	/**
	 * Count insights by one of their union-typed properties.
	 *
	 * Returns a COMPLETE record - every member of `keys` is present, defaulting
	 * to 0. The previous helper returned a partial Record<string, number> that
	 * was cast to Record<InsightType, number>, so consumers indexing a type that
	 * happened to have no insights got undefined out of a non-optional field.
	 * Values the backend writes that are outside the portal's union are ignored
	 * here but still counted in `totalInsights`.
	 */
	private static countByKey<K extends string>(
		insights: AgentInsight[],
		property: 'type' | 'priority',
		keys: readonly K[]
	): Record<K, number> {
		const counts = Object.fromEntries(keys.map(key => [key, 0])) as Record<K, number>;
		for (const insight of insights) {
			const key = insight[property] as unknown as K;
			if (key in counts) {
				counts[key] += 1;
			}
		}
		return counts;
	}

	// Get trending insights (most common types in last 7 days)
	static async getTrendingInsights(agentId?: string, days: number = 7): Promise<InsightTrend[]> {
		try {
			const startDate = new Date();
			startDate.setDate(startDate.getDate() - days);

			const constraints: QueryConstraint[] = [
				where('createdAt', '>=', Timestamp.fromDate(startDate))
			];

			if (agentId) {
				constraints.push(where('agent_id', '==', agentId));
			}

			const q = query(
				collection(db, 'agentInsights'),
				...constraints,
				orderBy('createdAt', 'desc'),
				firestoreLimit(this.STATS_SCAN_LIMIT)
			);
			const snapshot = await getDocs(q);

			const insights = snapshot.docs.map(doc => doc.data() as AgentInsight);
			const typeCount = this.countByKey(insights, 'type', INSIGHT_TYPES);

			return INSIGHT_TYPES
				.map(type => ({
					type,
					count: typeCount[type],
					trend: 'stable' as const // No previous-period comparison is computed yet
				}))
				.filter(entry => entry.count > 0)
				.sort((a, b) => b.count - a.count);
		} catch (error) {
			console.error('Error getting trending insights:', error);
			throw error;
		}
	}
}
